import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { UserCommunity, UserProfile as UserInfo } from './api/swagger'
import { MenuOption } from './components/Menus/DropDown'
import useLoadingContext from './hooks/useLoadingContext'
import { fetchUserRolesForCommunities } from './api/user'
import { useTranslation } from 'react-i18next'
import { useLoadingIds } from './hooks/useLoadingIds'
import { useMountEffect } from './hooks/useMountEffect'
import { useAuth } from './components/Routes/AuthProvider'
import {
  distinctRolesByRequest,
  RequestType,
} from './utils/distinctRolesByRequest'
import { useAppSnackbar } from './hooks/useAppSnackbar'

export const defaultValue = {
  user: undefined as UserInfo | undefined,
  setUser: (user?: UserInfo): void => {
    console.warn(
      `The default value of UserContext.setUser was called with user=${user}. Did you forget to install a UserProvider?`
    )
  },
  roles: [] as MenuOption[],
  uniqueActiveUserRoles: [] as UserCommunity[],
  selectedActorRole: {} as MenuOption,
  uniqueInactiveUserRoles: [] as UserCommunity[],
  allActiveRoles: [] as UserCommunity[],
  resetContextToDefaults: (): void => {
    console.warn(
      `The UserContext.resetContextToDefaults was called. Did you forget to use a UserProvider?`
    )
  },
  updateSelectedActorRole: (selectedActorRole: MenuOption): void => {
    console.warn(
      `The default value of UserContext.updateSelectedActorRole was called with selectedActorRole=${selectedActorRole}. Did you forget to install a UserProvider?`
    )
  },
  triggerFetchUserCommunities: (): void => {
    console.warn(
      `The default value of UserContext.triggerFetchUserCommunities was called. Did you forget to install a UserProvider?`
    )
  },
}

export type TestUserConfig = Partial<typeof defaultValue>

export const useUser = (): typeof defaultValue => useContext(UserContext)

export const UserContext = createContext(defaultValue)
UserContext.displayName = 'UserContext'

interface UserProviderProps extends PropsWithChildren {
  initialUser?: UserInfo
  testConfig?: TestUserConfig
}

export const UserProvider: React.FunctionComponent<UserProviderProps> = ({
  initialUser,
  children,
  testConfig,
}) => {
  const { t } = useTranslation()
  const {
    UserContext: { fetchUserCommunities: fetchUserCommunitiesLoadingId },
  } = useLoadingIds()
  const { handleError } = useAppSnackbar()
  const auth = useAuth()

  const [actorMenuOptions, setActorMenuOptions] = useState<MenuOption[]>([])
  const [selectedActorMenuOption, setSelectedActorMenuOption] =
    useState<MenuOption>()
  const [uniqueActiveUserRoles, setUniqueActiveUserRoles] = useState(
    Array<UserCommunity>()
  )
  const [uniqueInactiveUserRoles, setUniqueInactiveUserRoles] = useState(
    Array<UserCommunity>()
  )
  const [allActiveRoles, setAllActiveRoles] = useState(Array<UserCommunity>())

  const errorMessage = t(
    'UserContext.Error',
    'An error occurred while attempting load actors'
  )

  const fetchCurrentRoles = async () => {
    try {
      /** FIXME: Make the calls to roles within the UserContext */
      const fetchedRolesForCommunities = await fetchUserRolesForCommunities()

      const uniqueActiveUserRoles: UserCommunity[] = distinctRolesByRequest({
        userCommunitiesWithRoles: fetchedRolesForCommunities,
      })

      const allActiveRoles: UserCommunity[] = distinctRolesByRequest({
        userCommunitiesWithRoles: fetchedRolesForCommunities,
        includeOnlyActiveRoles: true,
        requestType: RequestType.All_ACTIVE_ROLES,
      })

      setAllActiveRoles(allActiveRoles)
      const hasActiveRoles = false
      const uniqueInactiveUserRoles = distinctRolesByRequest({
        userCommunitiesWithRoles: fetchedRolesForCommunities,
        includeOnlyActiveRoles: hasActiveRoles,
      })
      setUniqueInactiveUserRoles(uniqueInactiveUserRoles)
      const buildActorMenuOption = (
        userRole: UserCommunity & { actorKey: number }
      ): MenuOption => {
        const parts = [userRole.role]
        let yearsRoleIsValid = ''

        const duplicateRoles = uniqueActiveUserRoles.filter(
          (it) => it.role === userRole.role
        )

        if (duplicateRoles.length > 1) {
          const supervisorFullName = userRole.supervisorFirstName
            ? `${userRole.supervisorFirstName} ${
                userRole.supervisorLastName?.substring(0, 1) ?? ''
              }.`
            : ''

          if (supervisorFullName) {
            parts.push(supervisorFullName)
          }

          const duplicateRoleAndSupervisor = duplicateRoles.filter(
            (it) =>
              it.supervisorFirstName === userRole.supervisorFirstName &&
              it.supervisorLastName === userRole.supervisorLastName
          )

          if (duplicateRoleAndSupervisor.length > 1) {
            if (userRole.region) {
              parts.push(userRole.region)
            }

            const duplicateRoleAndSupervisorAndRegion =
              duplicateRoleAndSupervisor.filter(
                (it) => it.region === userRole.region
              )
            if (duplicateRoleAndSupervisorAndRegion.length > 1) {
              parts.push(`${userRole.actorKey}`)
            }

            if (userRole.validFrom && userRole.validTo) {
              yearsRoleIsValid = `${userRole.validFrom.getFullYear()}-${userRole.validTo.getFullYear()}`
            }
          }
        }

        return {
          name: parts.join(' / '),
          id: userRole.actorKey,
          subtitle: yearsRoleIsValid,
        }
      }

      const roles = uniqueActiveUserRoles.flatMap((it) => {
        if (it.actorKey) {
          return [
            // SAFETY: we know that we have a non null actorKey with the if statement we just did.
            buildActorMenuOption(it as UserCommunity & { actorKey: number }),
          ]
        }

        return []
      })

      roles.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))

      /**
       * Always give the parent role since we will login the user with their
       * lastSelectedActorKey and default to 'parent' if there was none.
       *
       * The userRoles endpoint will only return parent roles if there are
       * students/enrollments for the userKey.
       *
       * Roger is OK with this change.
       */
      roles.push({
        name: t('ActorSelector.Role.Parent', 'Parent'),
        id: 'parent',
      } as MenuOption)

      setActorMenuOptions(roles)
      setUniqueActiveUserRoles(uniqueActiveUserRoles)
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const { triggerFetch: triggerFetchUserCommunities } = useLoadingContext({
    asyncFunction: fetchCurrentRoles,
    loadingId: fetchUserCommunitiesLoadingId,
  })

  useMountEffect(triggerFetchUserCommunities)

  const getActorById = useCallback(
    (value: number | 'parent') =>
      actorMenuOptions.find((item) => item.id === value),
    [actorMenuOptions]
  )

  useEffect(() => {
    setSelectedActorMenuOption(getActorById(auth.userDetails.actingAs))
  }, [actorMenuOptions, auth.userDetails.actingAs, getActorById])

  const [userContextValue, setUserContextValue] = useState(() => {
    const setUser = (user?: UserInfo) => {
      // If torn down (which pretty much only happens in testing), actually calling setState will make React barf about "Warning: Can't perform a React state update on an unmounted component." plus another 10 lines or so of text. So, skip that.

      setUserContextValue((userContextValue) => {
        const isUnchanged = Object.is(user, userContextValue.user)
        return isUnchanged ? userContextValue : { ...userContextValue, user }
      })
    }

    return {
      user: initialUser,
      setUser,
    }
  })

  const resetContextToDefaults = () => {
    userContextValue.setUser(defaultValue.user)
  }
  return (
    <UserContext.Provider
      value={{
        ...userContextValue,
        roles: actorMenuOptions,
        uniqueActiveUserRoles,
        selectedActorRole: selectedActorMenuOption as MenuOption,
        updateSelectedActorRole: setSelectedActorMenuOption,
        uniqueInactiveUserRoles,
        allActiveRoles,
        resetContextToDefaults,
        triggerFetchUserCommunities,
        ...testConfig,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}
